import React, { useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'next-i18next';
import { useMedia } from '@spa/shared/hooks';
import { useFilterToQueryParameters, QUERY_PARAMS_KEYS } from '@spa/shared/hooks/search';

import { FiltersWithoutGroup, GlobalStyleDesktop, GlobalStyleMobile } from './styles';
import Modal from '@spa/shared/components/Modal';
import { countAggregatedFilters } from '@spa/shared/utils/filters';
import { PlacesGroup, TenantsGroup, LandlordGroup, TypologyGroup } from '@spa/shared/components/Filters/Groups';
import CheckboxFilter from '@spa/shared/components/Filters/Checkbox';
import Bedroom from '@spa/shared/components/Filters/Groups/Bedroom';
import House from '@spa/shared/components/Filters/Groups/House';
import Verification from '@spa/shared/components/Filters/Groups/Verification';
import { ClearFilters, ClearFiltersWrapper } from '@spa/shared/components/Search/FloatingBox/Filters/styles';
import SuitableFor from '@spa/shared/components/Filters/Groups/SuitableFor';
import Budget from '@spa/shared/components/Filters/Groups/Budget';
import { ResourcesContext } from '@spa/shared/contexts/resources';
import Button from '@spa/shared/components/Unisystem/Button';
import Registration from '@spa/shared/components/Filters/Groups/Registration';
import GenderPreferences from '@spa/shared/components/Filters/Groups/GenderPreferences';

const Places = PlacesGroup(['housing-type', 'rent-type']);
const Tenants = TenantsGroup(['guests', 'restrictions-occupancy']);
const Landlord = LandlordGroup(['min-response-rate', 'has-resident-landlord', 'max-response-rate']);
const Typology = TypologyGroup(['bedrooms', 'bathrooms', 'ensuite']);

const unsetPageField = fields => ({ ...fields, [QUERY_PARAMS_KEYS.PAGE]: undefined });

const FiltersModal = ({ onClose, numberOfFiltersApplied, setNumberOfFiltersApplied, location = {} }) => {
  const { count } = useContext(ResourcesContext);
  const { t } = useTranslation();
  const { is } = useMedia();
  const {
    fields,
    onChangeEventHandler,
    onChangeEventForceArrayHandler,
    onMultiChangeHandler,
    onChangeHandler,
    onChangeHandlerForceArray,
    clearFieldsHandler
  } = useFilterToQueryParameters({
    onChange: unsetPageField
  });
  const { histogram, currency } = useContext(ResourcesContext);
  const [numberOfFiltersToClear, setNumberOfFiltersToClear] = useState(numberOfFiltersApplied);

  useEffect(() => {
    const filtersLength = Object.keys(fields).reduce((acc, filter) => {
      return Array.isArray(fields[filter]) ? acc + countAggregatedFilters(fields, filter) : acc + 1;
    }, 0);

    setNumberOfFiltersApplied(filtersLength);
    setNumberOfFiltersToClear(filtersLength);
  }, [fields]);

  const footerInfo = (
    <ClearFiltersWrapper>
      <ClearFilters className={numberOfFiltersToClear ? null : 'disabled'} onClick={clearFieldsHandler}>
        {t('filters.labels.clearFilters', { number: numberOfFiltersToClear })}
      </ClearFilters>
      <Button isPrimary onClick={onClose}>
        {t('filters.labels.show_listings', { count: count })}
      </Button>
    </ClearFiltersWrapper>
  );

  const GlobalStyle = useMemo(() => (is.mobile ? GlobalStyleMobile : GlobalStyleDesktop), [is.mobile]);

  return (
    <Modal onClose={onClose} footer={footerInfo}>
      <GlobalStyle />
      <FiltersWithoutGroup>
        <CheckboxFilter
          isNew
          name="exclusive"
          value={true}
          onChange={onChangeEventHandler}
          selected={fields['exclusive']}
          label={t('global.labels.has-exclusive')}
        />
      </FiltersWithoutGroup>
      <Budget
        priceHistogram={histogram}
        fields={fields}
        currency={currency}
        onChangeEventHandler={onChangeEventHandler}
        onMultiChangeHandler={onMultiChangeHandler}
      />
      <Tenants
        fields={fields}
        onMultiChangeHandler={onMultiChangeHandler}
        onChangeEventForceArrayHandler={onChangeEventForceArrayHandler}
        onChangeHandler={onChangeHandler}
      />
      <Places
        onChangeEventHandler={onChangeEventHandler}
        onChangeEventForceArrayHandler={onChangeEventForceArrayHandler}
        fields={fields}
      />
      <Landlord fields={fields} onChangeEventHandler={onChangeEventHandler} />
      <GenderPreferences
        fields={fields}
        onChangeEventHandler={onChangeEventHandler}
        onChangeEventForceArrayHandler={onChangeEventForceArrayHandler}
      />
      <SuitableFor
        fields={fields}
        onChangeEventHandler={onChangeEventHandler}
        onChangeEventForceArrayHandler={onChangeEventForceArrayHandler}
      />
      <Registration fields={fields} onChangeEventHandler={onChangeEventHandler} location={location} />
      <Typology
        fields={fields}
        onChangeEventHandler={onChangeEventHandler}
        onChangeHandler={onChangeHandler}
        onChangeHandlerForceArray={onChangeHandlerForceArray}
      />
      <Bedroom fields={fields} onChangeEventHandler={onChangeEventForceArrayHandler} />
      <House fields={fields} onChangeEventHandler={onChangeEventForceArrayHandler} />
      <Verification fields={fields} onChangeEventHandler={onChangeEventHandler} />
    </Modal>
  );
};

FiltersModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  numberOfFiltersApplied: PropTypes.number,
  setNumberOfFiltersApplied: PropTypes.func,
  location: PropTypes.object
};

export default FiltersModal;
